/*
 * Rédelisse promotional-gift cart integrity.
 *
 * Scope, deliberately narrow:
 *   - identify promotional gift lines by their hidden line item property
 *   - keep the promotional gift at a maximum of one unit
 *   - remove a promotional gift that no longer has a qualifying parent product
 *   - expose the small helpers `product-form.js` needs to build a bundle add
 *
 * It does NOT price anything. The gift is made free by a Shopify automatic
 * Buy X Get Y discount; this module only manages cart membership and identity.
 *
 * It does NOT replace Dawn's cart code. Cart mutations go through the Ajax Cart
 * API and re-render the very sections Dawn's own components declare, so there is
 * no second cart pipeline and no hand-built cart markup.
 */
if (!window.RedelisseOffer) {
  window.RedelisseOffer = (function () {
    const GIFT_PROPERTY = '_redelisse_gift';
    const OFFER_PROPERTY = '_redelisse_offer';
    const PARENT_VARIANT_PROPERTY = '_redelisse_parent_variant';
    const PARENT_PRODUCT_PROPERTY = '_redelisse_parent_product';
    const GIFT_FLAG = 'true';
    const OFFER_ID = 'eye_massager_serum';
    const MAX_GIFT_QUANTITY = 1;
    const SOURCE = 'redelisse-offer';

    // Loop guard: our own cart writes publish `cartUpdate`, which would call us
    // straight back in. Reset in `finally` so a failure can never wedge it.
    let reconciling = false;

    function cartUrl(suffix) {
      const base = (window.routes && window.routes.cart_url) || '/cart';
      return `${base}${suffix}`;
    }

    function isGiftItem(item) {
      return Boolean(item && item.properties && item.properties[GIFT_PROPERTY] === GIFT_FLAG);
    }

    /* Properties stamped onto the gift line at add time. `_` prefixed, so Dawn
     * and Shopify hide them from customer-facing output while the admin order
     * still shows them. */
    function giftProperties(parentVariantId, parentProductId) {
      const properties = {};
      properties[GIFT_PROPERTY] = GIFT_FLAG;
      properties[OFFER_PROPERTY] = OFFER_ID;
      if (parentVariantId) properties[PARENT_VARIANT_PROPERTY] = String(parentVariantId);
      if (parentProductId) properties[PARENT_PRODUCT_PROPERTY] = String(parentProductId);
      return properties;
    }

    async function getCart() {
      const response = await fetch(cartUrl('.js'), { headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error(`Could not read the cart (${response.status})`);
      return response.json();
    }

    async function hasGift() {
      const cart = await getCart();
      return (cart.items || []).some(isGiftItem);
    }

    /* Qualification is checked at PRODUCT level, not variant level: the customer
     * may switch massager variant after taking the gift, and the Shopify
     * discount is itself configured against the product. The variant property is
     * kept as a fallback for gift lines added before the product id existed. */
    function qualifies(gift, item) {
      const properties = gift.properties || {};
      const parentProduct = properties[PARENT_PRODUCT_PROPERTY];
      if (parentProduct) return String(item.product_id) === String(parentProduct);

      const parentVariant = properties[PARENT_VARIANT_PROPERTY];
      if (parentVariant) return String(item.variant_id) === String(parentVariant);

      return false;
    }

    /* Reuse the section lists Dawn's own cart components declare, so we never
     * invent section ids or build cart HTML ourselves. */
    function renderTargets() {
      const targets = [];
      document.querySelectorAll('cart-drawer-items, cart-items').forEach((element) => {
        if (typeof element.getSectionsToRender !== 'function') return;
        try {
          targets.push(...element.getSectionsToRender());
        } catch (error) {
          /* A component whose section ids are not on this page: skip it. */
        }
      });
      return targets;
    }

    /* Dawn gates the whole empty-cart layout on an `is-empty` class, not on the
     * section HTML: `cart-drawer:not(.is-empty) .cart-drawer__warnings` hides the
     * "cart is empty" block, and `cart-drawer.is-empty .drawer__inner` supplies its
     * layout. `<cart-drawer>` and `#main-cart-footer` sit OUTSIDE the replaced
     * section, so re-rendering alone never switches them. This mirrors the exact
     * toggles CartItems.updateQuantity performs on a manual removal. */
    function toggleEmptyState(state) {
      if (!state || typeof state.item_count !== 'number') return;

      const isEmpty = state.item_count === 0;

      document.querySelectorAll('cart-drawer-items, cart-items').forEach((element) => {
        element.classList.toggle('is-empty', isEmpty);
      });

      const cartFooter = document.getElementById('main-cart-footer');
      if (cartFooter) cartFooter.classList.toggle('is-empty', isEmpty);

      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer) cartDrawer.classList.toggle('is-empty', isEmpty);
    }

    function applySections(state, targets) {
      if (!state || !state.sections) return;

      targets.forEach((target) => {
        const container = document.getElementById(target.id);
        if (!container) return;

        const source = state.sections[target.section];
        if (!source) return;

        const parsed = new DOMParser().parseFromString(source, 'text/html').querySelector(target.selector);
        if (!parsed) return;

        const destination = container.querySelector(target.selector) || container;
        destination.innerHTML = parsed.innerHTML;
      });
    }

    /* Gift lines are addressed by cart line KEY. An index shifts as the cart
     * changes, and a variant id cannot tell a gift apart from a paid line of the
     * same product. */
    async function changeLine(key, quantity) {
      const targets = renderTargets();
      const body = { id: key, quantity };
      const sections = [...new Set(targets.map((target) => target.section).filter(Boolean))];

      if (sections.length) {
        body.sections = sections.join(',');
        body.sections_url = window.location.pathname;
      }

      const response = await fetch(`${window.routes.cart_change_url}`, {
        ...fetchConfig(),
        body: JSON.stringify(body),
      });

      if (!response.ok) throw new Error(`Could not update the cart (${response.status})`);

      const state = await response.json();
      toggleEmptyState(state);
      applySections(state, targets);
      return state;
    }

    /* Idempotent: with a valid cart this performs no writes at all. */
    async function reconcile() {
      if (reconciling) return;
      reconciling = true;

      try {
        const cart = await getCart();
        const items = cart.items || [];
        const gifts = items.filter(isGiftItem);
        if (!gifts.length) return;

        const purchased = items.filter((item) => !isGiftItem(item));
        let changed = false;
        let state = cart;

        // Duplicate gift lines can only come from a race; keep the first.
        for (let index = 1; index < gifts.length; index += 1) {
          state = await changeLine(gifts[index].key, 0);
          changed = true;
        }

        const gift = gifts[0];

        if (!purchased.some((item) => qualifies(gift, item))) {
          // No qualifying parent left: the Shopify discount would stop applying
          // and the gift would silently become a paid item.
          state = await changeLine(gift.key, 0);
          changed = true;
        } else if (gift.quantity > MAX_GIFT_QUANTITY) {
          state = await changeLine(gift.key, MAX_GIFT_QUANTITY);
          changed = true;
        }

        if (changed && typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: SOURCE, cartData: state });
        }
      } catch (error) {
        console.error('Rédelisse offer: cart reconciliation failed', error);
      } finally {
        reconciling = false;
      }
    }

    function init() {
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          if (event && event.source === SOURCE) return;
          reconcile();
        });
      }

      // Catch carts changed elsewhere (another tab, back button from checkout).
      // Skipped for an empty cart so ordinary page views cost no extra request.
      const drawer = document.querySelector('cart-drawer');
      const emptyCart = drawer && drawer.classList.contains('is-empty');
      if (!emptyCart) reconcile();
    }

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', init, { once: true });
    } else {
      init();
    }

    return {
      GIFT_PROPERTY,
      OFFER_PROPERTY,
      PARENT_VARIANT_PROPERTY,
      PARENT_PRODUCT_PROPERTY,
      OFFER_ID,
      SOURCE,
      isGiftItem,
      giftProperties,
      getCart,
      hasGift,
      reconcile,
    };
  })();
}
